import {
  Badge,
  Button,
  DropdownListItem,
  Field,
  IconCheck20,
  IconKeyboardArrowDown20,
  Input,
  Spinner,
} from '@lightspeed/unified-components-helios-theme/react'
import { T, useT } from '@transifex/react'
import React, { useEffect, useRef, useState } from 'react'
import {
  AVAILABILITY_BADGE_APPEARANCES,
  AvailabilityBadgeAppearance,
  OutletOption,
  OutletSelectDropdownList,
  OutletSelectProps,
} from './OutletSelectTypes'
import { useOutletSelect } from './hooks/useOutletSelect'

type OutletSelectDropdownListProps = React.ComponentProps<
  typeof OutletSelectDropdownList
>
type OutletSelectRenderItemParams = Parameters<
  NonNullable<OutletSelectDropdownListProps['renderItem']>
>[0]
type OutletSelectRenderSectionParams = Parameters<
  NonNullable<OutletSelectDropdownListProps['renderSection']>
>[0]

const hasOutletAvailability = (
  outlet: OutletOption
): outlet is OutletOption & { available: number } =>
  typeof outlet.available === 'number'

const getAvailabilityBadgeAppearance = (
  available: number
): AvailabilityBadgeAppearance => {
  if (available <= 0) {
    return AVAILABILITY_BADGE_APPEARANCES.danger
  }

  if (available === 1) {
    return AVAILABILITY_BADGE_APPEARANCES.warning
  }

  return AVAILABILITY_BADGE_APPEARANCES.success
}

const AvailabilityBadge: React.FC<{ available: number }> = ({ available }) => {
  const appearance = getAvailabilityBadgeAppearance(available)

  return (
    <Badge appearance={appearance} customClasses={{ container: 'shrink-0' }}>
      {available < 0 ? (
        <T
          _str="{oversold, plural, one {{oversold} oversold} other {{oversold} oversold}}"
          oversold={-available}
          _context="Popover header"
        />
      ) : (
        <T
          _str="{available, plural, one {{available} available} other {{available} available}}"
          available={available}
          _context="Popover header"
        />
      )}
    </Badge>
  )
}

const OutletSummary: React.FC<{
  outlet: OutletOption
  isListItem?: boolean
  showAvailability?: boolean
}> = ({ outlet, isListItem = false, showAvailability = true }) => (
  <span className="flex min-w-0 flex-1 items-center gap-2">
    <span className="min-w-0 flex-1">
      <span
        className={`flex min-w-0 items-center text-left typography-body-sm-emphasized text-fg-neutral-default ${
          isListItem ? 'gap-3' : 'justify-between gap-2'
        }`}
      >
        <span className="min-w-0 flex-1 truncate">{outlet.name}</span>
        {showAvailability && hasOutletAvailability(outlet) && (
          <AvailabilityBadge available={outlet.available} />
        )}
      </span>
      {isListItem && outlet.address && (
        <span className="mt-0.5 block truncate text-left typography-body-xs text-fg-neutral-soft">
          {outlet.address}
        </span>
      )}
    </span>
  </span>
)

export const OutletSelect: React.FC<OutletSelectProps> = props => {
  const {
    className = '',
    showLabel = false,
    portalTargetRef: providedPortalTargetRef,
  } = props
  const t = useT()
  const fallbackPortalTargetRef = useRef<HTMLDivElement | null>(null)
  const portalTargetRef = providedPortalTargetRef ?? fallbackPortalTargetRef
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const model = useOutletSelect(props)

  useEffect(() => {
    if (providedPortalTargetRef) {
      return
    }

    // Portal the dropdown to the body so it escapes the fulfilment popover's
    // clipping/scroll container while keeping Helios base styles available.
    const portalTarget = document.createElement('div')
    portalTarget.className = 'uc-base wr-outlet-select-dropdown-portal'
    document.body.appendChild(portalTarget)
    fallbackPortalTargetRef.current = portalTarget

    return () => {
      fallbackPortalTargetRef.current = null
      portalTarget.remove()
    }
  }, [providedPortalTargetRef])

  useEffect(() => {
    if (isDropdownOpen) {
      searchInputRef.current?.focus()
    }
  }, [isDropdownOpen])

  if (!model.shouldRender) {
    return null
  }

  const fieldHintSlot = model.shouldShowOutletHint && (
    <span className="text-neutral-soft">
      {t('Customer will receive items from this outlet', {
        _context: 'Fulfillment outlet selector hint',
      })}
    </span>
  )

  const dropdownHeaderSlot = (
    <div className="w-full">
      <div className="w-full px-4">
        <Input
          ref={searchInputRef}
          type="search"
          value={model.searchTerm}
          labelSlot={t('Search outlets', {
            _context: 'Search input label for outlet picker.',
          })}
          placeholder={t('Enter outlet name', {
            _context: 'Search input placeholder',
          })}
          onChange={model.setSearchTerm}
          size="small"
          customClasses={{
            container: 'w-full',
            label: 'sr-only',
            controlContainer: 'w-full',
            inputBlock: 'min-w-0 flex-1',
            mainControl: 'w-full',
          }}
        />
      </div>
    </div>
  )

  const dropdownTriggerSlot = (
    <Button
      type="button"
      aria-label={t('Select outlet', {
        _context: 'Button aria label for outlet picker.',
      })}
      disabled={model.isDisabled}
      appearance="secondary"
      size="medium"
      block
      customClasses={{
        container: 'gap-3 bg-none hover:bg-none active:bg-none',
        content: 'flex-1',
      }}
      suffixSlot={
        <span className="flex size-5 shrink-0 items-center justify-center text-fg-neutral-soft">
          {model.isOutletOptionsLoading ? (
            <Spinner />
          ) : (
            <IconKeyboardArrowDown20 />
          )}
        </span>
      }
    >
      <OutletSummary
        outlet={model.triggerOutlet}
        showAvailability={!model.isDisabled}
      />
    </Button>
  )

  const dropdownFooterSlot = model.isOutletOptionsLoading ? (
    <div className="flex justify-center p-3">
      <Spinner />
    </div>
  ) : (
    model.filteredOutlets.length === 0 && (
      <div className="p-3 text-center typography-body-sm text-fg-neutral-soft">
        {model.isEmpty ? model.emptyLabel : model.noResultsLabel}
      </div>
    )
  )

  const renderDropdownItem = ({
    item,
    state,
  }: OutletSelectRenderItemParams) => {
    const outlet = model.outletById.get(item.value)

    if (!outlet) {
      return null
    }

    const isSelected = outlet.id === model.selectedOutlet?.id

    return (
      <DropdownListItem
        key={item.value}
        value={item.value}
        selected={isSelected}
        highlighted={state.highlighted}
        disabled={state.disabled}
        suffixSlot={
          isSelected && (
            <span className="flex size-5 text-fg-go-default">
              <IconCheck20 />
            </span>
          )
        }
        customClasses={{
          container:
            'min-h-[60px] items-center rounded-md px-2 py-2 hover:bg-go-highlight-default',
          content: 'min-w-0 flex-1',
          prefix: 'mr-2',
          suffix: 'ml-2',
        }}
      >
        <OutletSummary
          outlet={outlet}
          isListItem
          showAvailability={!model.isDisabled}
        />
      </DropdownListItem>
    )
  }

  const renderDropdownSection = ({
    section,
    children,
  }: OutletSelectRenderSectionParams) => (
    <React.Fragment key={section.items[0]?.value ?? 'outlets'}>
      {children}
    </React.Fragment>
  )

  return (
    <div className={`uc-base flex w-full flex-col font-general ${className}`}>
      <Field
        size="medium"
        customClasses={{
          root: 'w-full',
          label: 'typography-body-sm-emphasized',
          hint: 'typography-body-sm text-fg-neutral-soft',
        }}
        labelSlot={
          showLabel
            ? t('Fulfillment outlet', {
                _context: 'Mark as unfulfilled modal',
              })
            : undefined
        }
        hintSlot={fieldHintSlot}
      >
        <OutletSelectDropdownList
          trapFocus={false}
          onOpened={setIsDropdownOpen}
          items={model.items}
          portalTargetRef={portalTargetRef}
          positioning={{
            placement: 'bottom-start',
            gutter: 4,
            flip: false,
            sameWidth: true,
          }}
          onSelect={model.handleOutletSelect}
          customClasses={{
            positioner: 'wr-outlet-select-dropdown-positioner',
            container:
              'w-full box-border rounded-lg border border-border-neutral-soft bg-bg-neutral-top shadow-default',
            header: 'border-b border-border-neutral-soft p-2',
            content: 'max-h-[260px] overflow-y-auto p-1',
          }}
          headerSlot={dropdownHeaderSlot}
          triggerSlot={dropdownTriggerSlot}
          renderItem={renderDropdownItem}
          renderSection={renderDropdownSection}
          footerSlot={dropdownFooterSlot}
        />
      </Field>
    </div>
  )
}
